import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Collapse, Tooltip } from '@mui/material';

const CollapseCard = ({
  title,
  badge,
  dataCy,
  opened,
  children,
  onCollapse,
  classes = {
    buttonClassExtended: {},
  },
  tooltip = '',
  headerJustifyContent = 'space-between',
  id = new Date().getTime().toString(36),
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    if (onCollapse) {
      onCollapse(!expanded);
    }
    setExpanded(!expanded);
  };

  useEffect(() => {
    setExpanded(opened);
    if (onCollapse) {
      onCollapse(opened, true);
    }
  }, [opened, onCollapse, setExpanded]);

  return (
    <Box
      sx={{
        margin: `10px 0`,
        ...classes.root,
      }}
    >
      <Button
        id={id}
        fullWidth
        color="primary"
        variant="contained"
        sx={{
          '& .MuiButton-label': {
            justifyContent: 'space-between',
          },
          '& .MuiButton-endIcon': {
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            marginLeft: 'auto',
            transition: (theme) =>
              theme.transitions.create('transform', {
                duration: theme.transitions.duration.standard,
              }),
          },
          ...classes.buttonClass,
          ...(expanded ? classes.buttonClassExtended : {}),
        }}
        endIcon={<ExpandMoreIcon data-cy="expand-icon" />}
        onClick={handleExpandClick}
        data-cy={dataCy}
      >
        <Box display="flex" fontWeight="500" justifyContent={headerJustifyContent} width="100%">
          <Box display="flex" alignItems="center">
            {title}
            {tooltip && (
              <Tooltip title={tooltip}>
                <InfoIcon
                  sx={{
                    marginLeft: 11,
                    color: 'primary.main',
                    fontSize: (theme) => theme.typography.pxToRem(16),
                  }}
                />
              </Tooltip>
            )}
          </Box>
          {badge && badge}
        </Box>
      </Button>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            backgroundColor: 'common.white',
            borderRadius: (theme) => theme.palette.border.radius,
            border: (theme) => theme.palette.border.default,
            padding: '5px 15px',
            marginTop: 5,
            ...classes.box,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

CollapseCard.propTypes = {
  id: PropTypes.string,
  badge: PropTypes.node,
  opened: PropTypes.bool,
  dataCy: PropTypes.string,
  className: PropTypes.any,
  classes: PropTypes.object,
  tooltip: PropTypes.string,
  onCollapse: PropTypes.func,
  title: PropTypes.any.isRequired,
  children: PropTypes.node.isRequired,
  headerJustifyContent: PropTypes.string,
};

export default CollapseCard;
